import { useEffect, useRef, useState } from "react";
import { Search, Loader2, Save, MapPin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import GeoMap from "@/components/GeoMap";

const PRESETS = [100, 250, 500, 750, 1000];

export default function GeofenceBuilder() {
  const [clients, setClients] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [clientId, setClientId] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [radius, setRadius] = useState(500);
  const [maxRadius, setMaxRadius] = useState(1000);
  const [saving, setSaving] = useState(false);
  const [savedGeofences, setSavedGeofences] = useState([]);
  const searchTimer = useRef(null);

  useEffect(() => {
    api.get("/admin/clients").then((r) => {
      setClients(r.data);
      if (r.data[0]) setClientId(r.data[0].id);
    }).catch(() => {});
    api.get("/admin/settings").then((r) => setMaxRadius(r.data.max_radius_m || 1000)).catch(() => {});
    loadGeofences();
  }, []);

  const loadGeofences = () => api.get("/admin/geofences").then((r) => setSavedGeofences(r.data)).catch(() => {});

  useEffect(() => {
    if (!clientId) return;
    api.get("/admin/campaigns").then((r) => {
      const filtered = r.data.filter((c) => c.client_id === clientId);
      setCampaigns(filtered);
      setCampaignId(filtered[0]?.id || "");
    }).catch(() => {});
  }, [clientId]);

  const search = (q) => {
    setQuery(q);
    clearTimeout(searchTimer.current);
    if (q.trim().length < 3) { setResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=6&countrycodes=za&q=${encodeURIComponent(q)}`);
        setResults(await res.json());
      } catch {
        toast.error("Location search unavailable");
      } finally {
        setSearching(false);
      }
    }, 500);
  };

  const pick = (r) => {
    setSelected({ name: r.display_name.split(",")[0], address: r.display_name, lat: parseFloat(r.lat), lng: parseFloat(r.lon) });
    setResults([]);
    setQuery(r.display_name.split(",")[0]);
  };

  const onMapClick = async (latlng) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}`);
      const data = await res.json();
      setSelected({
        name: data.display_name?.split(",")[0] || "Dropped Pin",
        address: data.display_name || "",
        lat: latlng.lat,
        lng: latlng.lng,
      });
    } catch {
      setSelected({ name: "Dropped Pin", address: "", lat: latlng.lat, lng: latlng.lng });
    }
  };

  const save = async () => {
    if (!selected || !campaignId || !clientId) {
      toast.error("Select a client, campaign and location first");
      return;
    }
    setSaving(true);
    try {
      const campaign = campaigns.find((c) => c.id === campaignId);
      await api.post("/admin/geofences", {
        client_id: clientId,
        campaign_id: campaignId,
        location_name: selected.name,
        address: selected.address,
        city: selected.address.split(",").slice(-3, -1).join(",").trim() || "",
        country: "South Africa",
        latitude: selected.lat,
        longitude: selected.lng,
        radius_meters: radius,
        start_date: campaign?.start_date || "",
        end_date: campaign?.end_date || "",
        status: "ACTIVE",
      });
      toast.success(`Geofence saved: ${selected.name} (${radius}m)`);
      setSelected(null);
      setQuery("");
      loadGeofences();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const step = !clientId ? 1 : !campaignId ? 2 : !selected ? 3 : 4;

  return (
    <div className="space-y-6" data-testid="geofence-builder">
      <div>
        <p className="section-label">Geofence Builder</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight mt-1">Create a Geofence</h2>
      </div>

      <div className="flex items-center gap-2 text-[10px] font-bold tracking-widest text-slate-500 uppercase flex-wrap" data-testid="builder-workflow">
        {["Select Client", "Select Campaign", "Search Location", "Set Radius", "Save Geofence"].map((s, i) => (
          <span key={s} className="flex items-center gap-2">
            <span className={`px-2.5 py-1 rounded border ${i + 1 <= step + 1 ? "text-cyan-300 border-cyan-500/40 bg-cyan-500/10" : "border-white/10"}`}>{s}</span>
            {i < 4 && <span className="text-slate-600">→</span>}
          </span>
        ))}
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        <div className="space-y-5">
          <div className="card-surface p-5 space-y-4">
            <div>
              <Label className="text-slate-300 text-xs tracking-widest uppercase">1 · Client</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="mt-1.5 bg-secondary/50 border-white/10" data-testid="builder-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="bg-popover border-white/10">
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300 text-xs tracking-widest uppercase">2 · Campaign</Label>
              <Select value={campaignId} onValueChange={setCampaignId}>
                <SelectTrigger className="mt-1.5 bg-secondary/50 border-white/10" data-testid="builder-campaign-select"><SelectValue placeholder="Select campaign" /></SelectTrigger>
                <SelectContent className="bg-popover border-white/10">
                  {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="card-surface p-5 space-y-3">
            <Label className="text-slate-300 text-xs tracking-widest uppercase">3 · Search Google-Style Location</Label>
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
              <Input value={query} onChange={(e) => search(e.target.value)} placeholder="Search addresses, malls, stadiums..."
                className="pl-9 bg-secondary/50 border-white/10" data-testid="builder-search-input" />
              {searching && <Loader2 className="absolute right-3 top-3 w-4 h-4 animate-spin text-cyan-400" />}
            </div>
            {results.length > 0 && (
              <div className="rounded-md border border-white/10 bg-secondary/40 divide-y divide-white/5 max-h-56 overflow-y-auto" data-testid="builder-search-results">
                {results.map((r) => (
                  <button key={r.place_id} onClick={() => pick(r)} className="w-full text-left px-3 py-2.5 hover:bg-white/5 transition-colors" data-testid="builder-search-result">
                    <p className="text-sm text-white font-medium flex items-center gap-1.5"><MapPin className="w-3 h-3 text-cyan-400 shrink-0" /> {r.display_name.split(",")[0]}</p>
                    <p className="text-[11px] text-slate-500 truncate">{r.display_name}</p>
                  </button>
                ))}
              </div>
            )}
            <p className="text-[11px] text-slate-500">Or click anywhere on the map to drop a pin.</p>
            {selected && (
              <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 p-3" data-testid="builder-selected-location">
                <p className="text-sm font-semibold text-white">{selected.name}</p>
                <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{selected.address}</p>
                <p className="text-[11px] font-mono text-cyan-300 mt-1">{selected.lat.toFixed(6)}, {selected.lng.toFixed(6)}</p>
              </div>
            )}
          </div>

          <div className="card-surface p-5 space-y-4">
            <Label className="text-slate-300 text-xs tracking-widest uppercase">4 · Geofence Radius</Label>
            <div className="flex flex-wrap gap-2">
              {PRESETS.filter((p) => p <= maxRadius).map((p) => (
                <button key={p} onClick={() => setRadius(p)}
                  className={`px-3 py-1.5 rounded text-xs font-bold border transition-colors ${radius === p ? "bg-blue-600 border-blue-500 text-white" : "border-white/10 text-slate-400 hover:border-cyan-500/40"}`}
                  data-testid={`radius-preset-${p}`}>
                  {p === 1000 ? "1km" : `${p}m`}
                </button>
              ))}
            </div>
            <div>
              <div className="flex justify-between text-xs text-slate-400 mb-2">
                <span>Custom radius</span>
                <span className="font-mono text-cyan-300" data-testid="radius-value">{radius}m</span>
              </div>
              <Slider value={[radius]} onValueChange={([v]) => setRadius(v)} min={100} max={maxRadius} step={50} data-testid="radius-slider" />
            </div>
            <Button onClick={save} disabled={saving || !selected} className="w-full bg-blue-600 hover:bg-blue-500 font-semibold" data-testid="save-geofence-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> SAVE &amp; ACTIVATE GEOFENCE</>}
            </Button>
          </div>
        </div>

        <GeoMap
          geofences={savedGeofences}
          draftMarker={selected ? { lat: selected.lat, lng: selected.lng } : null}
          draftRadius={radius}
          onMapClick={onMapClick}
          height="calc(100vh - 220px)"
          testid="builder-map"
        />
      </div>
    </div>
  );
}
